import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import LinearProgress from "@mui/material/LinearProgress";
import Typography from "@mui/material/Typography";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import PieChartIcon from "@mui/icons-material/PieChart";
import SpeedIcon from "@mui/icons-material/Speed";
import TableChartIcon from "@mui/icons-material/TableChart";

import { withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { Bar, BarChart, CartesianGrid, Cell, LabelList, Legend, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import fpDashboardService from "@plannel/services/fp/fp-dashboard-service";
import FpGridSetting from "@plannel/pages/factory-plan/common/grid";
import VersionSearchGroup from "@plannel/pages/factory-plan/common/component/VersionSearchGroup";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import ChartComposed from "@plannel/components/rechart/ChartComposed";
import { FullScreenButton } from "@plannel/components/ActionIconButton";
import dateUtils from "@plannel/utils/date-util";
import reduxUtil from "@plannel/utils/redux-util";
import { useMenuAccess } from "@plannel/hooks/useMenuAccess";
import debug from "@plannel/utils/debug-util";

// old OnTimeRate.jsx(details) — 진행바(BorderLinearProgress) 색: 바 #2499ef / 트랙 rgb(0 0 0 / 13%).
const TILE_BAR_COLOR = "#2499ef";
const TILE_TRACK_COLOR = "rgba(0, 0, 0, 0.13)";
// old DeliveryStatusSummary.jsx theme 상수 — 납기준수(primary)/지연(warning)/실패(danger).
const PIE_COLOR_OK = "#3B7DDD";
const PIE_COLOR_LATE = "#fcb92c";
const PIE_COLOR_SHORT = "#dc3545";
// old LeadTime.jsx — 가로 막대 색.
const LEAD_TIME_BAR_COLOR = "#ffc604";
// old 테마 fpChartPriority.color2(Bed) — ItemCycleTime.js가 확정한 hex 재사용.
// color3(Worker)는 확정 hex 선례가 없어 FP 리포트 트렌드 라인 표준색(#4472C4,
// MaterialPsi/PsiDetail/InoutStatus 재고 라인)으로 배정(2026-08-12 계획 승인 — 사람 확인 항목).
const BED_LINE_COLOR = "#6DE36F";
const WORKER_LINE_COLOR = "#4472C4";

// TODO(MIG-T2): [simulationkpidashboard/SimulationKPIDashboard] 차트 축 눈금 간격 지정 미이관.
//   old: 가동률 `scales.y.ticks.stepSize = 20`(Worker/Bed), 리드타임 `getHorizontalScales()`의
//        x축 `ticks.stepSize = 0.5`
//   사유: recharts에 눈금 간격 직접 지정 대응이 없다(tickCount 근사만 가능) — 축 범위는
//        아래 yAxisDomain으로 old(suggestedMax 100)와 맞췄고 눈금 위치만 자동 계산된다.
// old 가동률 차트 y축: min 0 + suggestedMax 100(부하율 %는 100이 의미 있는 기준선이라 데이터가
// 작아도 축을 100까지 유지). 과부하(100 초과)는 잘리면 안 되므로 상한을 데이터 최대값과 비교한다.
const UTIL_CHART_OPTIONS = {
  // 이 카드는 페이지 폭의 ~33%(1920 기준 내부 약 627px)라 ChartComposed 기본 minWidth "760px"가
  // 그대로 걸리면 ResponsiveContainer div가 760px로 고정되고, MUI Card(기본 overflow:hidden)가
  // 넘치는 ~130px(격자·라인 꼬리·마지막 라벨)을 스크롤바 없이 잘라낸다. 좁은 카드에
  // ChartComposed를 넣는 다른 모듈 대시보드의 관용구(RpScenarioComp/DpStatus/IPComparison/
  // MpScenarioCom)와 동일하게 %로 덮어써 px 하한을 무력화한다 — 컨테이너 width가 98%라
  // 33%는 절대 걸리지 않는다.
  minWidth: "33%",
  minHeight: 180,
  // right 30 → 60: x축이 날짜(yyyy-MM-dd)고 라인 1개라 recharts가 scalePoint을 써서 마지막
  // 눈금이 플롯 우측 끝에 놓인다(preserveEnd는 마지막 눈금을 항상 남긴다). ChartComposed의
  // XAxis는 textAnchor를 주지 않아 라벨이 눈금 중앙 정렬되므로 fontSize 14 기준 폭 ~76px의
  // 절반 ~39px이 플롯 밖으로 나간다 → 30이면 마지막 한 글자가 SVG viewBox에 잘린다.
  // old Chart.js의 layout.padding.right 60(SimulationKpiWorkerResrcUtil.jsx:49)을 그대로 가져온다.
  margin: { left: 30, top: 30, bottom: 10, right: 60 },
  yAxisDomain: [0, (dataMax) => Math.max(100, dataMax)],
};

// old 위젯(SimulationKpiWorkerResrcUtil.jsx)에는 차트 설정(⚙) 버튼이 없다. 게다가 ChartComposed의
// p13n 저장 키 chartId는 시리즈 field만 이어붙여 만드는데(ChartComposed.js:86-92), 작업자/작업대가
// 같은 utilSeries(field "rate")를 써서 둘 다 "rateChart"가 되고 viewName(활성 탭 reduxKey)도 같다
// → 한쪽에서 저장하면 다른 쪽 차트 타입/activeFlg까지 바뀐다. FP report 화면 18개와 RP/IP/MP/DP
// 비교 대시보드가 모두 그러듯 팝업을 끈다.
const UTIL_SHOW_CHART_CONFIG = false;

// 딥링크 대상(§13-1 sender) — TabMenuList의 reduxKey이자 그 화면의 viewName prop.
const RESOURCE_UTILIZATION_VIEW = "FP_RESOURCE_UTILIZATION";

// 전체화면(카드 1개만 확대) 대상 식별자 — 다른 saas 대시보드(DashboardComponent 기반 IP/DP/RP)와
// 동일하게 카드마다 버튼을 둔다.
const CARD = {
  FULFILL: "fulfillRate",
  ONTIME: "onTimeRate",
  DELIVERY: "deliveryStatus",
  LEAD_TIME: "leadTime",
  WORKER: "workerUtil",
  BED: "bedUtil",
  PROBLEM: "problemGrid",
};

// old wingui-core getFormattedNumber 대응 — 천단위 + 소수 최대 2자리.
// FP 대시보드 트랙 기준을 5005 InOutStatusDashboard와 동일하게 맞춘다(공용 numberUtil의 기본값
// 3자리를 쓰면 서버가 2자리로 반올림한 값에 없던 자릿수가 붙어 보인다).
const formatNumber = (value) => {
  if (value == null || value === "") return value;
  const num = Number(value);
  return Number.isNaN(num) ? value : num.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

// old KpiProblemDetailGrid valueCallback — "코드 (명칭)" 결합(양쪽 trim, 빈값 방어).
const joinCodeName = (cd, nm) => {
  const code = String(cd ?? "").trim();
  const name = String(nm ?? "").trim();
  if (!code) return name;
  if (!name) return code;
  return `${code} (${name})`;
};

const SimulationKPIDashboard = ({ t, viewName, title }) => {
  const reduxDispatch = useDispatch();
  const { isPermitted } = useMenuAccess();

  // VersionSearchGroup(controlled) — 캐스케이드 각 단계가 부분 객체로 통지(PlanResult.js 선례).
  // 딥링크 수신 1/2 — 마운트 시 viewState 초기값으로 프리필을 받는다(ResourceUtilization.js 와 같은 구조).
  // 비활성 탭은 언마운트되므로(TabContainer 의 TabPanel) 닫힌 탭·열린 비활성 탭 모두 이 경로로 받는다 — 발신처는
  // AI 시나리오 확정 후 결과 화면 이동 [D-104]·SimulationCompare. 아래 incomingNav(2/2)는 이 화면이 이미 활성일 때만.
  // 프리필이 없으면(평상시) 기존 기본값 그대로다. ⚠️ 탭을 닫을 때까지 viewState 에 남아 재활성 때 다시 프리필된다.
  const [versionState, setVersionState] = useState(() => {
    const vs = reduxUtil.getViewState(viewName);   // 초기화 1회만 — getViewState 는 매번 cloneDeep 한다
    return {
      planScope: vs?.planScope ?? "",
      planningDate: vs?.planningDate ? new ZDate(vs.planningDate) : null,
      versionCd: vs?.versionCd ?? "",
      plantCd: Array.isArray(vs?.plantCd) ? vs.plantCd : [],
      selectedVersion: null
    };
  });

  // 응답 원본: 타일 2개 = raw number, 파이 = KpiResultDto, 리드타임/가동률 = {labels, data} 맵.
  const [fulfillRate, setFulfillRate] = useState(null);
  const [onTimeRate, setOnTimeRate] = useState(null);
  const [deliveryStatus, setDeliveryStatus] = useState(null);
  const [leadTime, setLeadTime] = useState(null);
  const [workerUtil, setWorkerUtil] = useState(null);
  const [bedUtil, setBedUtil] = useState(null);
  const [problemRows, setProblemRows] = useState([]);

  // gridRef는 로딩 오버레이용(debounce 클로저에서도 항상 현재 인스턴스를 가리키는 ref가 필요),
  // gridApi state는 컬럼 정의 적용용이다 — 전체화면 전환으로 그리드가 재마운트되면 api 객체가
  // 새로 생기고, 그때 아래 effect가 다시 돌아 새 인스턴스에도 컬럼이 적용된다.
  const gridRef = useRef();
  const [gridApi, setGridApi] = useState(null);

  // 전체화면 — 공용 FullScreenButton(components/ActionIconButton) 계약대로 boolean + 대상 id 2개.
  // 켜지면 검색영역은 그대로 두고 본문만 해당 카드 하나로 교체한다(IP/DP/RP 대시보드가 쓰는
  // DashboardComponent.js:177-199와 동일 방식 — 모달이 아니다).
  const [fullScreen, setFullScreen] = useState(false);
  const [fullScreenCardId, setFullScreenCardId] = useState(null);

  // 전체화면 전환은 본문을 카드 하나로 "교체"하는 방식이라 그리드가 다른 부모 아래에서 다시
  // 그려진다 → React가 AgGridReact를 재마운트해 사용자가 바꾼 컬럼 순서/폭/정렬이 통째로
  // 사라진다(GridUtils.setColumnDefs의 상태 보존은 같은 인스턴스에서만 유효 — 새 인스턴스는
  // 캡처 시점에 컬럼이 0개다). 전환 직전에 저장해 뒀다가 새 인스턴스가 준비되면 되돌린다.
  // (2026-08-13 브라우저 검증에서 5009와 함께 발견·수정)
  const columnStateRef = useRef(null);
  const captureColumnState = () => {
    const api = gridRef.current?.api;
    if (api && !api.isDestroyed()) columnStateRef.current = api.getColumnState();
  };

  // 응답 순서 뒤바뀜 방지 토큰 — 병렬 API 7개가 한 조회 단위라 토큰은 1개(fp-frontend.md §0-8).
  const latestReqRef = useRef(0);
  // debounce 콜백(최초 렌더 클로저)에서 항상 최신 조회조건을 읽기 위한 ref(PlanResult.js 선례).
  const searchStateRef = useRef(versionState);
  searchStateRef.current = versionState;

  // 딥링크 수신(fp-master-screen-guide.md §13-2) — old의 `location.state` 수신 초기화 대응.
  // SimulationCompare(생산 계획 버전 분석)의 "계획 결과 요약" 버튼이 계획범위/계획수립일/버전/공장을
  // 채워서 이 화면을 연다. 별도 조회 호출은 필요 없다 — versionCd/plantCd가 바뀌면 아래
  // [versionState.versionCd, versionState.plantCd] effect가 자동조회를 트리거한다.
  // VersionSearchGroup(controlled)은 planScope/planningDate가 바뀌면 캐스케이드를 다시 돌리는데,
  // 넘어온 versionCd가 그 목록에 있으면 그대로 유지하므로 프리필이 덮이지 않는다.
  const incomingNav = useSelector((state) => state.historyState.history);
  useEffect(() => {
    if (incomingNav !== viewName) return;
    const freshState = reduxUtil.getViewState(viewName);
    if (!freshState) return;

    setVersionState((prev) => ({
      ...prev,
      planScope: freshState.planScope ?? prev.planScope,
      // viewStates에는 "yyyy-MM-dd" 문자열로 저장돼 온다(§13-1 sender 관례) — Date로 되돌린다.
      planningDate: freshState.planningDate ? new ZDate(freshState.planningDate) : prev.planningDate,
      versionCd: freshState.versionCd ?? prev.versionCd,
      plantCd: freshState.plantCd ?? prev.plantCd,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingNav]);

  const defaultGridMemo = useMemo(() => {
    const settings = FpGridSetting({ title, viewName });
    return {
      ...settings,
      // 읽기 전용 대시보드(old setNoneEditableGrid 대응) — 저장/삭제/DataState 추적 없음.
      defaultColDef: { ...settings.defaultColDef, editable: false },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearData = () => {
    setFulfillRate(null);
    setOnTimeRate(null);
    setDeliveryStatus(null);
    setLeadTime(null);
    setWorkerUtil(null);
    setBedUtil(null);
    setProblemRows([]);
  };

  // TODO(MIG-T2): [simulationkpidashboard/SimulationKPIDashboard] 수동 재조회(전역 검색 버튼) 미이관.
  //   old: globalButtons "search" → versionSearchGroupRef.handleSubmit(loadData) — 검색 버튼을
  //        눌러야만 7개 API가 재조회됐다(버전 변경만으로는 재조회 안 됨)
  //   사유: new 대시보드 선례에 전역 버튼 바가 없음(5007 판정과 동일). 대신 버전/plant 변경 시
  //        즉시 자동조회로 연결(Report 트랙 "선택형=자동조회" 규칙) — 조회 기능 자체는 유지된다.
  // 제외(MIG): old DashboardPanel 인프라는 이관 제외 확정(fp-dashboard-screen-guide.md §0-1) —
  //   actionBar(레이아웃 목록/저장/리셋/설정, store:'DB' dashboard/save|view|list|delete)와
  //   위젯 닫기(X)/최대화/전체화면 버튼 전부. new는 고정 레이아웃이라 대응 개념이 없다.
  // 제외(MIG): AUTO_LOAD 뷰옵션 분기는 old 인프라(useViewStore) 계열로 미이관 — new는 버전
  //   캐스케이드가 확정되면 항상 자동조회한다. Popover 우상단 REFRESH(plan-dts 재조회) 아이콘은
  //   new 공용 VersionSearchGroup에 없는 기능(공용 컴포넌트 소관)이라 이 화면에서 다루지 않는다.
  const loadData = () => {
    const { planScope, versionCd, plantCd } = searchStateRef.current;
    if (!versionCd) return;

    const requestToken = ++latestReqRef.current;
    // 서버가 읽는 필드만 전송(§A-3) — plantCd 배열은 서버 필드명 plantCds로 리네임(old 동일).
    const params = { planScope, versionCd, plantCds: plantCd };

    gridRef.current?.api?.setGridOption("loading", true);

    fpDashboardService.getSimulationKpiFulfillRate(params)
      .then((response) => {
        if (requestToken !== latestReqRef.current) return;
        setFulfillRate(Number(response?.data ?? 0));
      })
      .catch((e) => {
        if (requestToken !== latestReqRef.current) return;
        setFulfillRate(null);
        debug(e);
      });

    fpDashboardService.getSimulationKpiOnTimeRate(params)
      .then((response) => {
        if (requestToken !== latestReqRef.current) return;
        setOnTimeRate(Number(response?.data ?? 0));
      })
      .catch((e) => {
        if (requestToken !== latestReqRef.current) return;
        setOnTimeRate(null);
        debug(e);
      });

    // old는 res.data null 가드가 없어 빈 응답이면 TypeError를 catch가 삼켰다 — null 가드로 정정.
    fpDashboardService.getProblemKpi(versionCd, params)
      .then((response) => {
        if (requestToken !== latestReqRef.current) return;
        setDeliveryStatus(response?.data ?? null);
      })
      .catch((e) => {
        if (requestToken !== latestReqRef.current) return;
        setDeliveryStatus(null);
        debug(e);
      });

    fpDashboardService.getSimulationKpiLeadTime(params)
      .then((response) => {
        if (requestToken !== latestReqRef.current) return;
        setLeadTime(response?.data ?? null);
      })
      .catch((e) => {
        if (requestToken !== latestReqRef.current) return;
        setLeadTime(null);
        debug(e);
      });

    // bed/worker는 백엔드가 동일 구현이라 응답이 100% 같다(old 백엔드도 동일 — 회귀 아님,
    // fp-dashboard-service.js 주석 참고). 버전에 자원 데이터가 없으면 {}(labels/data 키 없음).
    fpDashboardService.getSimulationKpiUsedRateWorker(params)
      .then((response) => {
        if (requestToken !== latestReqRef.current) return;
        setWorkerUtil(response?.data ?? null);
      })
      .catch((e) => {
        if (requestToken !== latestReqRef.current) return;
        setWorkerUtil(null);
        debug(e);
      });

    fpDashboardService.getSimulationKpiUsedRateBed(params)
      .then((response) => {
        if (requestToken !== latestReqRef.current) return;
        setBedUtil(response?.data ?? null);
      })
      .catch((e) => {
        if (requestToken !== latestReqRef.current) return;
        setBedUtil(null);
        debug(e);
      });

    fpDashboardService.getProblemStatusDetail(versionCd, params)
      .then((response) => {
        if (requestToken !== latestReqRef.current) return;
        setProblemRows(Array.isArray(response?.data) ? response.data : []);
        gridRef.current?.api?.setGridOption("loading", false);
      })
      .catch((e) => {
        if (requestToken !== latestReqRef.current) return;
        setProblemRows([]);
        gridRef.current?.api?.setGridOption("loading", false);
        debug(e);
      });
  };

  // VersionSearchGroup 캐스케이드(planScope→계획수립일→버전→plant)가 여러 렌더에 걸쳐 순차
  // 커밋되며 각 단계가 조회를 중복 트리거하는 것을 200ms debounce로 합친다(PlanResult.js 선례).
  const debouncedLoadRef = useRef();
  if (!debouncedLoadRef.current) {
    debouncedLoadRef.current = debounce(() => loadData(), 200);
  }
  useEffect(() => () => debouncedLoadRef.current.cancel(), []);

  // 선택형 조회조건 = 즉시 자동조회(§B-1). plantCd는 캐스케이드가 항상 새 배열로 통지하므로
  // 버전 재선택(같은 코드)에도 effect가 돌지만 debounce + 동일 조건 재조회라 무해하다.
  useEffect(() => {
    if (!versionState.versionCd) {
      debouncedLoadRef.current.cancel();
      // 진행 중이던 조회의 응답이 아래 clearData()를 덮어쓰지 않도록 토큰을 무효화한다(§0-8).
      // (버전이 없는 planScope로 바꾸면 재조회가 없어, 안 하면 이전 스코프 데이터가 그대로 남는다.)
      latestReqRef.current += 1;
      gridRef.current?.api?.setGridOption("loading", false);
      clearData();
      return;
    }
    debouncedLoadRef.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versionState.versionCd, versionState.plantCd]);

  // Worker/Bed 카드 더블클릭 → 자원 가동률 보고서로 딥링크(fp-master-screen-guide.md §13-1 sender).
  // old는 차트 요소 위 더블클릭이었지만 state 페이로드가 클릭 요소와 무관해(요소 존재 여부만
  // 게이트) 카드 본문 더블클릭으로 대응 — ChartComposed(공용)는 클릭 이벤트 prop이 없다.
  // 반드시 updateViewState → updateHistoryState 순서(§13-1).
  const openResourceUtilization = () => {
    const { planScope, planningDate, versionCd, plantCd } = searchStateRef.current;
    if (!versionCd) return;

    const navFields = {
      planScope,
      planningDate: planningDate ? dateUtils.formatDate(planningDate, "yyyy-MM-dd") : null,
      versionCd,
      plantCd,
    };
    reduxDispatch(reduxUtil.updateViewState(reduxUtil.createObjectState(RESOURCE_UTILIZATION_VIEW, navFields)));
    reduxDispatch(reduxUtil.updateHistoryState(RESOURCE_UTILIZATION_VIEW));
  };

  // 문제 현황 상세 — old problemDetailGridItems 15개(표시 10 + 숨김 5) 1:1. 폭은 old(60~100)
  // 비율 유지 + 소폭 확장(fp-frontend.md §0-4). 숨김 컬럼의 headerName " "도 old 그대로.
  // TODO(MIG-T2): [simulationkpidashboard/SimulationKPIDashboard] plantNm 세로 셀 병합 미이관.
  //   old: mergeRule {criteria:"value"} — 같은 공장명 연속 행을 한 셀로 병합
  //   사유: 셀 병합은 Tier2 명시 항목(saas-fp/docs/fp-frontend-migration/CLAUDE.md) — 값 반복 표시로 정보는 동일하다.
  // TODO(MIG-T2): [simulationkpidashboard/SimulationKPIDashboard] 컬럼 값 목록 필터 5종 미이관.
  //   old: plantNm/woCd/inventoryNm/problemTpCd/problemReasonTpCd에 RealGrid setFilters
  //   사유: 컬럼 필터는 Tier2 명시 항목. old도 필터 목록을 데이터 로드 전에만 생성해 항상 빈
  //        목록이었던 결함이 있어(KpiProblemDetailGrid.jsx setGridOptions) 실사용 기능이 아니었다.
  const problemColumnDefs = useMemo(() => [
    { headerName: " ", field: "plantCd", hide: true },
    { headerName: "FP_PLANT", field: "plantNm", width: 120 },
    { headerName: "FP_WO_CD", field: "woCd", width: 120 },
    { headerName: " ", field: "inventoryCd", hide: true },
    {
      headerName: "FP_INVENTORY_NM", field: "inventoryNm", width: 160,
      valueGetter: (params) => joinCodeName(params.data?.inventoryCd, params.data?.inventoryNm),
    },
    { headerName: "FP_REQUEST_QTY", field: "requestQty", width: 90, type: ["numberColumn", "rightAligned"] },
    // old dataType:"datetime"은 로케일 dateFormat+timeFormat(초까지)로 표시됐고, 백엔드 dueDate도
    // LocalDateTime이라 시각이 실제 데이터다(주문 납기는 00:00이 아닐 수 있음) → 날짜만 보여주면
    // 정보가 잘린다. 읽기 전용이므로 편집기가 딸린 dateTimeColumn 타입 대신 audit 컬럼 선례
    // (Route.js:148)와 동일하게 valueFormatter만 건다.
    {
      headerName: "FP_DUE_DT", field: "dueDate", width: 150,
      type: ["nonEditableColumn", "centerAligned"], valueFormatter: dateUtils.formatDateTime,
    },
    {
      headerName: "FP_PROBLEM_TP_CD", field: "problemTpCd", width: 120,
      // old displayCallback: transLangKey(problemTpCd) — 코드 값을 그대로 랭키로 번역(키 없으면 원문).
      valueFormatter: (params) => (params.value ? t(params.value) : ""),
    },
    {
      headerName: "FP_PROBLEM_REASON_TP_CD", field: "problemReasonTpCd", width: 140,
      valueFormatter: (params) => (params.value ? t(params.value) : ""),
    },
    {
      headerName: "FP_PROBLEM_ITEM_NM", field: "problemInventoryNm", width: 160,
      valueGetter: (params) => joinCodeName(params.data?.problemInventoryCd, params.data?.problemInventoryNm),
    },
    { headerName: " ", field: "problemInventoryCd", hide: true },
    { headerName: " ", field: "problemRouteCd", hide: true },
    {
      headerName: "FP_PROBLEM_ROUTE_NM", field: "problemRouteNm", width: 140,
      valueGetter: (params) => joinCodeName(params.data?.problemRouteCd, params.data?.problemRouteNm),
    },
    { headerName: " ", field: "problemResourceCd", hide: true },
    {
      headerName: "FP_PROBLEM_RESOURCE_NM", field: "problemResourceNm", width: 150,
      valueGetter: (params) => joinCodeName(params.data?.problemResourceCd, params.data?.problemResourceNm),
    },
  ], [t]);

  // 정적 컬럼이지만 onGridReady 1회가 아니라 effect로 태운다 — 언어 전환(t) 시 헤더 재번역 +
  // 전체화면 전환으로 그리드가 재마운트될 때 새 api에 컬럼 재적용.
  useEffect(() => {
    if (!gridApi) return;
    GridUtils.setColumnDefs({ api: gridApi, columnDefs: problemColumnDefs, viewName, initState: false });
  }, [gridApi, problemColumnDefs, viewName]);

  // 재마운트 직후 1회만 컬럼 상태를 되돌린다. deps를 [gridApi]로 좁힌 것이 핵심 —
  // 위 effect와 합치거나 problemColumnDefs를 deps에 넣으면 언어를 전환할 때 "마지막 전환
  // 시점의 낡은 상태"가 그 사이 사용자가 바꾼 현재 순서를 덮어쓴다. 위 effect가 선언 순서상
  // 먼저 돌아 컬럼 정의가 적용된 뒤에 실행된다(initState:false 경로엔 await가 없어 동기 완료).
  useEffect(() => {
    if (!gridApi || !columnStateRef.current) return;
    gridApi.applyColumnState({ state: columnStateRef.current, applyOrder: true });
  }, [gridApi]);

  // 파이(납기 현황) — old DeliveryStatusSummary.jsx: [okCnt, lateCnt, shortCnt] 3슬라이스,
  // datalabels 없음, 범례 좌측. name은 여기서 번역해 범례/툴팁에 그대로 쓴다.
  const deliveryPieData = useMemo(() => {
    if (!deliveryStatus) return [];
    return [
      { name: t("FP_ONTIME_DELIVERY"), value: Number(deliveryStatus.okCnt ?? 0), color: PIE_COLOR_OK },
      { name: t("FP_DELIVERY_DELAY"), value: Number(deliveryStatus.lateCnt ?? 0), color: PIE_COLOR_LATE },
      { name: t("FP_DELIVERY_SHORT"), value: Number(deliveryStatus.shortCnt ?? 0), color: PIE_COLOR_SHORT },
    ];
  }, [deliveryStatus, t]);
  const pieHasData = deliveryPieData.some((slice) => slice.value > 0);

  // 리드타임 — 서버가 {labels(inventoryCd 상위 10, 평균 내림차순), data(일수)}로 내려준다.
  const leadTimeRows = useMemo(() => {
    const labels = leadTime?.labels ?? [];
    const values = leadTime?.data ?? [];
    return labels.map((label, index) => ({ inventoryCd: label, days: Number(values[index] ?? 0) }));
  }, [leadTime]);

  // 가동률 — 서버가 {labels(yyyy-MM-dd), data(평균 usedRate)}로 내려준다(버전 없으면 {}).
  const buildUtilRows = (util) => {
    const labels = util?.labels ?? [];
    const values = util?.data ?? [];
    return labels.map((label, index) => ({ label, rate: Number(values[index] ?? 0) }));
  };
  const workerRows = useMemo(() => buildUtilRows(workerUtil), [workerUtil]);
  const bedRows = useMemo(() => buildUtilRows(bedUtil), [bedUtil]);

  // TODO(MIG-T2): [simulationkpidashboard/SimulationKPIDashboard] 가동률 차트 값 라벨(datalabels) 미이관.
  //   old: dataset.datalabels {display:true, align:'top', offset:10} — 라인 점 위에 값 실표시
  //        (chartjs-plugin-datalabels, wingui/src/index.js:36 전역 등록으로 실동작)
  //   사유: new 대응 ChartComposed가 recharts LabelList 미지원 — 공용 컴포넌트(DP/RP/IP 공유)라
  //        이 트랙에서 수정하지 않음(2026-08-11 사용자 지시, 5007 판정과 동일). 값은 툴팁으로 확인.
  // TODO(MIG-T2): [simulationkpidashboard/SimulationKPIDashboard] 범례 클릭 시 시리즈 숨김 토글 미이관.
  //   old: ChartLegendPlugin(DOM 커스텀 범례) — li 클릭 시 dataset show/hide + 취소선
  //   사유: recharts 기본 Legend/ChartComposed에 대응 훅이 없음(Report 트랙 §A-7 Tier2 확정과 동일).
  //        시리즈가 1개뿐이라 실익도 없다.
  const utilSeries = useMemo(() => ([
    {
      id: "rate", field: "rate", text: t("FP_AVG_UTILIZATION"), type: "line",
      // old customTitle y축 제목 "(부하율: %)" — ChartComposed는 groupName이 곧 y축 라벨이다.
      groupName: `(${t("FP_LOAD_RATIO")}: %)`,
      typeOptions: { strokeWidth: 2 },
    },
  ]), [t]);
  const workerSeries = useMemo(
    () => utilSeries.map((series) => ({ ...series, typeOptions: { ...series.typeOptions, stroke: WORKER_LINE_COLOR } })),
    [utilSeries]
  );
  const bedSeries = useMemo(
    () => utilSeries.map((series) => ({ ...series, typeOptions: { ...series.typeOptions, stroke: BED_LINE_COLOR } })),
    [utilSeries]
  );

  // 카드 헤더 45px + 우측 전체화면 버튼. 제목은 그대로 가운데 정렬을 유지해야 해서 버튼만
  // absolute로 띄운다(FillRateDashboard.js:526-534가 SettingsButton을 배치하는 방식과 동일).
  const cardHeader = (Icon, label, cardId) => (
    <Box sx={{ position: "relative", display: "flex", height: "45px", alignItems: "flex-end", justifyContent: "center" }}>
      <Icon fontSize={"medium"} sx={{ mb: 0.2 }} />
      <Typography className="chart-title" variant="h6" sx={{ pl: 1, textAlign: "center", fontWeight: 500 }}>
        {label}
      </Typography>
      <Box sx={{ position: "absolute", right: 0, mr: 0.5 }}>
        {/* setter를 그대로 넘기지 않고 캡처를 얹은 래퍼를 넘긴다 — 공용 FullScreenButton의
            props 계약은 그대로 두고 재마운트 직전 컬럼 상태만 챙긴다. */}
        <FullScreenButton
          fullScreen={fullScreen && fullScreenCardId === cardId}
          setFullScreen={(next) => { captureColumnState(); setFullScreen(next); }}
          screenId={cardId}
          setScreenId={(next) => { captureColumnState(); setFullScreenCardId(next); }}
        />
      </Box>
    </Box>
  );

  const noChartData = (
    <Typography variant="subtitle1" sx={{ p: 3, height: "80%", textAlign: "center", fontWeight: 500 }}>
      {t("noChartData")}
    </Typography>
  );

  // KPI 진행바 타일 — old OnTimeRate.jsx(details)의 BorderLinearProgress + 흰색 % 라벨 오버레이를
  // MUI LinearProgress로 재현(2026-08-12 계획 승인 — 화면 로컬, 공용 추출은 5009/5001 때 재판단).
  const rateTile = (cardId, label, rawRate, sx) => {
    const rate = Number(rawRate ?? 0);
    return (
      <Card sx={{ flex: 1, height: "100%", ...sx }}>
        {cardHeader(SpeedIcon, label, cardId)}
        <Box sx={{ display: "flex", alignItems: "center", height: "calc(100% - 45px)", px: "4rem" }}>
          <Box sx={{ position: "relative", width: "100%" }}>
            <LinearProgress
              variant="determinate"
              value={Math.max(0, Math.min(100, rate))}
              sx={{
                height: 28,
                borderRadius: "10px",
                backgroundColor: TILE_TRACK_COLOR,
                "& .MuiLinearProgress-bar": { backgroundColor: TILE_BAR_COLOR, borderRadius: "5px" },
              }}
            />
            <Typography
              sx={{
                position: "absolute", inset: 0, display: "flex", alignItems: "center",
                justifyContent: "center", color: "#fff", fontSize: 20,
              }}
            >
              {`${formatNumber(rate)} %`}
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  };

  // 카드 1개를 그리는 함수 — 일반 레이아웃과 전체화면(카드 하나만 100%) 양쪽에서 같은 정의를 쓴다.
  const renderCard = (cardId, sx) => {
    switch (cardId) {
      // 제외(MIG): old 계획 충족률 타일의 라벨 더블클릭은 onDblClick prop 미전달로 TypeError가
      //   나던 버그(SimulationKpiFulfillRate.jsx — 동작한 적 없는 기능)라 이관하지 않는다
      //   (fp-dashboard-screen-guide.md §0-8 "old 버그" 계열).
      case CARD.FULFILL:
        return rateTile(CARD.FULFILL, t("FP_PLAN_FULFILLMENT_RATE"), fulfillRate, sx);

      // TODO(MIG-T2): [simulationkpidashboard/SimulationKPIDashboard] 납기준수 계획율 타일
      //   더블클릭 → 주문 추적(UI_FP_ORDER_TRACKING) 딥링크 미이관.
      //   old: drillDownTo → history.push(state:{planScope, planningDate, versionCd, plantCd})
      //   사유: 대상 화면(ordertracking/OrderTracking)이 미이관 — 화면 개발 시 연계(사용자 지시).
      case CARD.ONTIME:
        return rateTile(CARD.ONTIME, t("FP_ONTIME_PLAN_RATE"), onTimeRate, sx);

      // old 위젯 7개 중 유일하게 제목이 없던 카드 — 통일된 look을 위해 랭팩
      // FP_DELIVERY_STATUS_SUMMARY("납기 현황 요약")로 헤더를 추가한다(2026-08-12 계획 승인).
      case CARD.DELIVERY:
        return (
          <Card sx={sx}>
            {cardHeader(PieChartIcon, t("FP_DELIVERY_STATUS_SUMMARY"), CARD.DELIVERY)}
            <Box width={"100%"} height={"calc(100% - 45px)"}>
              {pieHasData ? (
                <ResponsiveContainer width={"100%"} height={"100%"}>
                  <PieChart>
                    <Pie dataKey="value" isAnimationActive={false} data={deliveryPieData} cx="50%" cy="50%" outerRadius={"80%"}>
                      {deliveryPieData.map((slice) => (
                        <Cell key={`cell-${slice.name}`} fill={slice.color} />
                      ))}
                    </Pie>
                    <Tooltip isAnimationActive={false} formatter={(value) => formatNumber(value)} />
                    <Legend layout="vertical" align="left" verticalAlign="middle" />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                noChartData
              )}
            </Box>
          </Card>
        );

      // TODO(MIG-T2): [simulationkpidashboard/SimulationKPIDashboard] 리드타임 막대 더블클릭 →
      //   자원 간트(UI_FP_GANTT_RESOURCE) 딥링크 미이관.
      //   old: 클릭한 막대의 inventoryCd를 state에 얹어 history.push
      //   사유: 대상 화면(ResourceGantt)이 1차 제외(Gantt 라이브러리 팀 논의) — 개발 시 연계.
      case CARD.LEAD_TIME:
        return (
          <Card sx={sx}>
            {cardHeader(LeaderboardIcon, t("FP_PRDT_LEAD_TIME"), CARD.LEAD_TIME)}
            <Box width={"100%"} height={"calc(100% - 45px)"}>
              {leadTimeRows.length > 0 ? (
                <ResponsiveContainer width={"100%"} height={"100%"}>
                  {/* recharts 직접 사용 — 가로 막대는 ChartComposed 미지원, FillRateDashboard.js:446 선례 */}
                  <BarChart layout="vertical" data={leadTimeRows} margin={{ top: 10, right: 40, left: 10, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                      type="number"
                      fontSize={12}
                      tickFormatter={(value) => formatNumber(value)}
                      label={{ value: `(${t("FP_DAY")})`, position: "insideBottomRight", offset: -12, fontSize: 12 }}
                    />
                    <YAxis
                      dataKey="inventoryCd"
                      type="category"
                      width={95}
                      fontSize={12}
                      label={{ value: `(${t("FP_ITEM")})`, position: "insideTopLeft", offset: 0, fontSize: 12 }}
                    />
                    <Tooltip isAnimationActive={false} formatter={(value) => formatNumber(value)} />
                    <Bar dataKey="days" name={t("FP_PRDT_LEAD_TIME")} fill={LEAD_TIME_BAR_COLOR} isAnimationActive={false}>
                      {/* old ChartDataLabelPlugin — 막대 안쪽 끝 흰색 값 라벨(실동작 기능, 2026-08-12 계획 승인) */}
                      <LabelList dataKey="days" position="insideRight" fill="#ffffff" fontSize={12} formatter={(value) => formatNumber(value)} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                noChartData
              )}
            </Box>
          </Card>
        );

      case CARD.WORKER:
        return (
          <Card sx={sx}>
            {cardHeader(LeaderboardIcon, t("FP_WORKER_UTILIZATION"), CARD.WORKER)}
            <Box width={"100%"} height={"calc(100% - 45px)"}
              sx={{ cursor: isPermitted(RESOURCE_UTILIZATION_VIEW) ? "pointer" : "default" }}
              onDoubleClick={isPermitted(RESOURCE_UTILIZATION_VIEW) ? openResourceUtilization : undefined}>
              {workerRows.length > 0 ? (
                <ChartComposed xAxis={"label"} data={workerRows} dataSeries={workerSeries} options={UTIL_CHART_OPTIONS} tooltipAnimation={false} showConfigurationPopup={UTIL_SHOW_CHART_CONFIG} />
              ) : (
                noChartData
              )}
            </Box>
          </Card>
        );

      case CARD.BED:
        return (
          <Card sx={sx}>
            {cardHeader(LeaderboardIcon, t("FP_BED_UTILIZATION"), CARD.BED)}
            <Box width={"100%"} height={"calc(100% - 45px)"}
              sx={{ cursor: isPermitted(RESOURCE_UTILIZATION_VIEW) ? "pointer" : "default" }}
              onDoubleClick={isPermitted(RESOURCE_UTILIZATION_VIEW) ? openResourceUtilization : undefined}>
              {bedRows.length > 0 ? (
                <ChartComposed xAxis={"label"} data={bedRows} dataSeries={bedSeries} options={UTIL_CHART_OPTIONS} tooltipAnimation={false} showConfigurationPopup={UTIL_SHOW_CHART_CONFIG} />
              ) : (
                noChartData
              )}
            </Box>
          </Card>
        );

      case CARD.PROBLEM:
        return (
          <Card sx={sx}>
            {cardHeader(TableChartIcon, t("FP_PROBLEM_STATUS_DETAIL"), CARD.PROBLEM)}
            <Box className="ag-theme-balham" width={"100%"} height={"calc(100% - 45px)"}>
              <AgGridReact ref={gridRef} rowData={problemRows} {...defaultGridMemo} onGridReady={(params) => setGridApi(params.api)} />
            </Box>
          </Card>
        );

      default:
        return null;
    }
  };

  // old 레이아웃(12컬럼: 좌 w4=33%/우 w8=67%, 좌 4:5:7행 / 우 8:8행)을 % 비율로 환산 — §B-3 골격.
  return (
    <Box className={"grid-area"} sx={{ display: "flex", flexDirection: "column", width: "100%", height: "100%", minHeight: 560 }}>
      <FilterContainer hasIcons={false}>
        <VersionSearchGroup
          value={versionState}
          onChange={(partial) => setVersionState((prev) => ({ ...prev, ...partial }))}
        />
      </FilterContainer>
      {fullScreen && fullScreenCardId ? (
        // 전체화면 — 검색영역은 그대로 두고 본문만 해당 카드 하나로 교체한다(IP/DP/RP 대시보드가
        // 쓰는 DashboardComponent.js:177-199와 동일. 확대 중에도 버전을 바꿔 재조회할 수 있다).
        <Box sx={{ display: "flex", width: "100%", flex: 1, minHeight: 0 }}>
          {renderCard(fullScreenCardId, { width: "100%", height: "100%" })}
        </Box>
      ) : (
        <Box sx={{ display: "flex", width: "100%", flex: 1, minHeight: 0 }}>
          <Box sx={{ width: "calc(33.33% - 12px)", height: "100%", mr: 1.5 }}>
            <Box sx={{ display: "flex", height: "calc(25% - 8px)", mb: 1.5 }}>
              {renderCard(CARD.FULFILL, { mr: 1.5 })}
              {renderCard(CARD.ONTIME)}
            </Box>
            {renderCard(CARD.DELIVERY, { width: "100%", height: "calc(31.25% - 8px)", mb: 1.5 })}
            {renderCard(CARD.LEAD_TIME, { width: "100%", height: "calc(43.75% - 8px)" })}
          </Box>
          <Box sx={{ width: "66.67%", height: "100%" }}>
            <Box sx={{ display: "flex", width: "100%", height: "calc(50% - 6px)", mb: 1.5 }}>
              {renderCard(CARD.WORKER, { width: "calc(50% - 6px)", height: "100%", mr: 1.5 })}
              {renderCard(CARD.BED, { width: "calc(50% - 6px)", height: "100%" })}
            </Box>
            {renderCard(CARD.PROBLEM, { width: "100%", height: "calc(50% - 6px)" })}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default withTranslation()(SimulationKPIDashboard);
